// German dictionary (Deutsch). Translated from the canonical English
// dictionary in ./en.ts. Missing keys gracefully fall back to English at
// runtime.
import type { Dictionary } from "../index";

export const de: Dictionary = {
  // ---- App / brand ----
  "app.name": "HomeBook",
  "app.tagline": "Familiengerechte Buchungen",

  // ---- Common ----
  "common.signin": "Anmelden",
  "common.signup": "Registrieren",
  "common.signout": "Abmelden",
  "common.cancel": "Abbrechen",
  "common.save": "Speichern",
  "common.delete": "Löschen",
  "common.edit": "Bearbeiten",
  "common.add": "Hinzufügen",
  "common.loading": "Laden…",
  "common.search": "Suchen",
  "common.filters": "Filter",
  "common.clearAll": "Alle zurücksetzen",
  "common.none": "Keine",
  "common.optional": "optional",
  "common.required": "erforderlich",
  "common.total": "Gesamt",
  "common.back": "Zurück",
  "common.publish": "Veröffentlichen",
  "common.hide": "Verbergen",
  "common.allTypes": "Alle Typen",
  "common.any": "Alle",
  "common.noImage": "Kein Bild",

  // ---- Header / nav ----
  "nav.browse": "Entdecken",
  "nav.documentation": "Dokumentation",
  "nav.myTrips": "Meine Reisen",
  "nav.hostPanel": "Gastgeber-Bereich",
  "nav.admin": "Admin",
  "nav.listProperty": "+ Unterkunft einstellen",
  "nav.menu": "Menü",
  "nav.myTripsShort": "Meine Reisen",
  "nav.hostPanelShort": "Gastgeber-Bereich",
  "nav.adminShort": "Admin-Konsole",

  // ---- Roles ----
  "role.ADMIN": "Website-Administrator",
  "role.PRODUCT_ADMIN": "Unterkunftsgeber",
  "role.USER": "Gast",

  // ---- Reservation status ----
  "status.PENDING": "Ausstehend",
  "status.APPROVED": "Genehmigt",
  "status.REJECTED": "Abgelehnt",
  "status.CANCELLED": "Storniert",

  // ---- Booking types ----
  "booking.PRIVATE": "Privat",
  "booking.SHARED": "Geteilt",
  "booking.PRIVATE.short": "Gesamte Unterkunft",
  "booking.SHARED.short": "Geteilter Aufenthalt",
  "booking.PRIVATE.desc": "Buchen Sie die gesamte Unterkunft für sich. Festpreis pro Nacht; die Daten werden bei Genehmigung gesperrt.",
  "booking.SHARED.desc": "Mehrere Gruppen können gleichzeitig übernachten. Die Kosten der Unterkunft werden unter allen belegenden Gruppen anteilig pro Person und Tag aufgeteilt.",
  "booking.field.partySize": "Gruppengröße (Anzahl Gäste)",
  "booking.field.partySizeHint": "Wie viele Personen sind in Ihrer Gruppe?",
  "booking.privateTitle": "Private Buchung",
  "booking.onlySharedHint": "Diese Tage enthalten bereits einen geteilten Aufenthalt — diese Buchung kann nur geteilt sein.",
  "booking.onlyPrivateHint": "Diese Tage überschneiden einen privaten Aufenthalt — das ganze Haus ist nötig.",
  "booking.sharedTitle": "Geteilte Buchung — Kosten anteilig",
  "detail.privateBlockHint": "Diese Termine sind durch eine genehmigte private (exklusive) Buchung belegt — bitte andere Termine wählen.",
  "detail.bookedLegend": "Rot = exklusiver Privataufenthalt (ganzes Haus) · Violett = geteilter Aufenthalt (Umlage, Anzahl Gäste angegeben).",
  "booking.coOccupants": "Mitbelegende Gruppen an diesen Daten",
  "booking.noCoOccupants": "Bisher keine weiteren genehmigten Gruppen an diesen Daten — Sie würden den vollen Nachtprice zahlen, wenn Sie allein genehmigt würden.",
  "booking.partyN": "{count} {noun}",
  "booking.partyNoun": "Gast",
  "partyNounPlural": "Gäste",
  "booking.occupantsToday": "{count} Gäste gebucht",
  "booking.yourShare": "Ihr voraussichtlicher Anteil",
  "booking.yourShareHint": "Der Endpreis wird bei Genehmigung neu berechnet, basierend auf allen genehmigten Mitbelegenden.",
  "booking.perDay": "pro Tag",
  "booking.split": "{share} ({party} von {total} Gästen)",
  "booking.capacityExceeded": "Diese Daten sind bereits ausgebucht.",
  "booking.immutableLocked": "Der Buchungstyp ist gesperrt, sobald Reservierungen existieren.",

  // ---- Property types ----
  "property.HOUSE": "Haus",
  "property.APARTMENT": "Wohnung",
  "property.VILLA": "Villa",
  "property.CHALET": "Chalet",
  "property.STUDIO": "Studio",
  "property.COTTAGE": "Ferienhaus",

  // ---- Auth modal ----
  "auth.welcome": "Willkommen bei HomeBook",
  "auth.subtitle": "Melden Sie sich an, um Aufenthalte zu buchen, oder erstellen Sie ein Konto, um zu beginnen.",
  "auth.tabSignin": "Anmelden",
  "auth.tabRegister": "Konto erstellen",
  "auth.demoHint": "Demo: admin / admin123 · guest / guest123",
  "auth.signingIn": "Anmeldung…",
  "auth.creating": "Konto wird erstellt…",
  "auth.createAccount": "Konto erstellen",

  // Auth fields
  "auth.field.username": "Benutzername",
  "auth.field.usernamePlaceholder": "jane_doe",
  "auth.field.usernameHint": "3-30 Zeichen, Buchstaben/Zahlen/_/-, muss mit einem Buchstaben beginnen.",
  "auth.field.firstName": "Vorname",
  "auth.field.lastName": "Nachname",
  "auth.field.email": "E-Mail",
  "auth.field.emailPlaceholder": "you@example.com",
  "auth.field.phone": "Telefon (optional)",
  "auth.field.phonePlaceholder": "+1-555-0100",
  "auth.field.password": "Passwort",
  "auth.field.passwordPlaceholder": "••••••••",
  "auth.field.passwordHint": "Mindestens 6 Zeichen",

  // Username availability
  "username.checking": "Verfügbarkeit wird geprüft…",
  "username.available": "Benutzername verfügbar",
  "username.taken": "Dieser Benutzername ist bereits vergeben",
  "username.invalid": "Der Benutzername muss 3-30 Zeichen lang sein, mit einem Buchstaben beginnen und darf nur Buchstaben, Zahlen, _ oder - enthalten.",
  "username.tooShort": "Benutzername ist zu kurz",

  // ---- Browse view ----
  "browse.heroBadge": "Familiengerechte Buchungen",
  "browse.heroTitle": "Buchen Sie ein Familienhaus, Chalet oder eine Wohnung",
  "browse.heroSubtitle": "Eigentümer stellen ihre Unterkünfte mit Fotos und Verfügbarkeitsdaten ein. Gäste fordern Reservierungen an. Gastgeber und der Website-Administrator validieren jede Buchung — nach Genehmigung sind diese Daten gesperrt.",
  "browse.searchPlaceholder": "Nach Titel, Ort oder Beschreibung suchen…",
  "browse.resultsCount": "{count} {plural} verfügbar",
  "browse.resultSingular": "Unterkunft",
  "browse.resultPlural": "Unterkünfte",
  "browse.loadingResults": "Laden…",
  "browse.noResultsTitle": "Keine Unterkünfte entsprechen Ihrer Suche",
  "browse.noResultsBody": "Versuchen Sie, einige Filter zu entfernen oder andere Daten zu suchen.",
  "browse.signInToReserve": "Melden Sie sich an, um einen Aufenthalt zu reservieren.",
  "browse.activeFilters": "Aktive Filter:",
  "browse.guestsChip": "{count}+ Gäste",

  // Filters
  "filter.propertyType": "Unterkunftstyp",
  "filter.guests": "Gäste",
  "filter.checkIn": "Anreise",
  "filter.checkOut": "Abreise",

  // ---- Product card ----
  "card.perNight": "/Nacht",
  "card.bedrooms": "{count} Schlafzimmer{plural}",
  "card.bathrooms": "{count} Badezimmer{plural}",
  "card.guests": "{count} Gäste max.",

  // ---- Product detail ----
  "detail.backToAll": "Zurück zu allen Unterkünften",
  "layers.title": "Wer sieht was",
  "layers.axisTop": "mehr Zugriff",
  "layers.axisBottom": "weniger Zugriff",
  "layers.adminScope": "alles, überall",
  "layers.ownerScope": "volle Kontrolle über ihre Häuser",
  "layers.staffScope": "ein zugewiesenes Haus: Buchungen + Rechnungen",
  "layers.userScope": "eigene Reservierungen + zugewiesene Rechnungen",
  "layers.chHouses": "Häuser",
  "layers.chBookings": "Buchungen",
  "layers.chBills": "Rechnungen",
  "layers.chUsers": "Benutzer",
  "layers.chSettings": "Einstellungen",
  "layers.chStaff": "Personal",
  "layers.chMyTrips": "Meine Reisen",
  "layers.chAssignedBills": "Zugewiesene Rechnungen",
  "layers.legendAdmin": "Alles, jedes Haus",
  "layers.legendOwner": "Volle Kontrolle über ihre Häuser",
  "layers.legendStaff": "Ein zugewiesenes Haus: Buchungen + Rechnungen, nie die Hausverwaltung",
  "layers.legendUser": "Eigene Reisen + zugewiesene Rechnungen",
  "pd.manage": "Dieses Haus verwalten",
  "pd.changePhoto": "Dieses Foto ändern",
"pd.addPhoto": "Foto hinzufügen",
"pd.photoUpdated": "Foto aktualisiert",
  "pd.staff": "Haus-Team",
"pd.staffHint": "Zugewiesene Moderatoren können Buchungen dieses Hauses bestätigen und dessen Rechnungen verwalten — und sehen nichts anderes.",
"pd.addStaff": "Moderator hinzufügen…",
"pd.staffEmpty": "Noch keine Moderatoren zugewiesen.",
"pd.staffRemove": "Aus diesem Haus entfernen",
"pd.staffAdded": "Moderator zu diesem Haus hinzugefügt",
"pd.staffRemoved": "Moderator entfernt",
  "detail.about": "Über diese Unterkunft",
  "detail.amenities": "Ausstattung",
  "detail.availability": "Verfügbarkeitsfenster",
  "detail.noAvailability": "Bisher keine Verfügbarkeitsfenster veröffentlicht.",
  "detail.alreadyBooked": "Bereits gebucht",
  "detail.perNight": "pro Nacht",
  "detail.checkIn": "Anreise",
  "detail.checkOut": "Abreise",
  "detail.guestName": "Gastname",
  "detail.guestEmail": "E-Mail",
  "detail.guestPhone": "Telefon (optional)",
  "detail.notes": "Notizen (optional)",
  "detail.notesPlaceholder": "Anzahl Kinder, Ankunftszeit, besondere Wünsche…",
  "detail.priceRow": "{price} × {nights} Nacht{plural}",
  "detail.requestReservation": "Reservierung anfordern",
  "detail.signInToReserve": "Anmelden zum Reservieren",
  "detail.requesting": "Anfrage wird gesendet…",
  "detail.requestHelpText": "Ihre Anfrage wird zur Validierung an den Gastgeber gesendet. Genehmigte Daten werden für alle anderen gesperrt.",

  // ---- My trips ----
  "trips.title": "Meine Reisen",
  "trips.subtitle": "Ihre Reservierungsanfragen und ihr Status. Gastgeber validieren jede Anfrage, bevor sie bestätigt wird.",
  "trips.upcoming": "Anstehend",
  "trips.history": "Verlauf",
  "trips.signInTitle": "Melden Sie sich an, um Ihre Reisen zu sehen",
  "trips.signInBody": "Ihre Reservierungsanfragen und ihr Validierungsstatus erscheinen hier.",
  "trips.emptyTitle": "Noch keine Reservierungen",
  "trips.emptyBody": "Durchsuchen Sie Unterkünfte und fordern Sie einen Aufenthalt an, um zu beginnen.",
  "trips.cancelRequest": "Anfrage stornieren",
  "trips.validatedBy": "Validiert von {name}",

  // ---- Host panel ----
  "host.title": "Gastgeber-Bereich",
  "host.subtitleAdmin": "Website-Administrator — alle Unterkünfte verwalten und jede Reservierung validieren.",
  "host.subtitleOwner": "Verwalten Sie Ihre Unterkünfte und validieren Sie Reservierungsanfragen von Gästen.",
  "host.tabProperties": "Unterkünfte",
  "host.tabReservations": "Reservierungen",
  "host.pendingCount": "{count} ausstehend",
  "host.listProperty": "Unterkunft einstellen",
  "host.noPropertiesTitle": "Noch keine Unterkünfte",
  "host.noPropertiesBody": "Erstellen Sie Ihre erste Unterkunft, um Reservierungen zu erhalten.",
  "host.edit": "Bearbeiten",
  "host.availability": "Verfügbarkeit",
  "host.photos": "Photos ({count})",
  "host.delete": "Löschen",
  "host.published": "Veröffentlicht",
  "host.hidden": "Verborgen",
  "host.noAvailabilitySet": "Keine Verfügbarkeit festgelegt",
  "host.availWindows": "{count} Verfügbarkeitsfenster{plural}",
  "host.awaitingDecision": "Wartet auf Ihre Entscheidung",
  "host.noPending": "Derzeit keine ausstehenden Anfragen.",
  "host.allRequests": "Alle Anfragen",
  "host.noOther": "Keine weiteren Reservierungen.",
  "host.approve": "Genehmigen",
  "host.decline": "Ablehnen",
  "host.deleteTitle": "Diese Unterkunft löschen?",
  "host.deleteBody": "Dadurch werden die Unterkunft, ihre Fotos, Verfügbarkeitsfenster und zugehörige Reservierungen entfernt. Diese Aktion kann nicht rückgängig gemacht werden.",

  // ---- Availability modal ----
  "avail.title": "Verfügbarkeit verwalten",
  "avail.description": "Fügen Sie die Zeiträume hinzu, in denen Gäste diese Unterkunft reservieren können. Reservierungen müssen vollständig in ein Fenster fallen.",
  "avail.from": "Verfügbar ab",
  "avail.to": "Verfügbar bis",
  "avail.addWindow": "Fenster hinzufügen",
  "avail.current": "Aktuelle Fenster ({count})",
  "avail.empty": "Noch keine Verfügbarkeit. Fügen Sie oben ein Fenster hinzu, damit Gäste buchen können.",

  // ---- Image manager ----
  "images.title": "Fotos verwalten",
  "images.description": "Laden Sie Fotos Ihrer Unterkunft hoch (JPG, PNG, WebP — max. 8 MB). Das erste Foto wird als Deckbild verwendet.",
  "images.caption": "Beschriftung (optional)",
  "images.photos": "Photos ({count})",
  "images.empty": "Noch keine Fotos.",
  "images.cover": "Deckbild",

  // ---- Product editor ----
  "productEditor.newTitle": "Neue Unterkunft einstellen",
  "productEditor.editTitle": "Unterkunft bearbeiten",
  "productEditor.description": "Füllen Sie die Details aus. Sie können nach dem Speichern Fotos und Verfügbarkeitsdaten hinzufügen.",
  "productEditor.title": "Titel",
  "productEditor.titlePlaceholder": "The Martin Family House",
  "productEditor.descriptionLabel": "Beschreibung",
  "productEditor.location": "Ort",
  "productEditor.locationPlaceholder": "12 Maple Lane, Greenwood Hills",
  "productEditor.type": "Unterkunftstyp",
  "productEditor.bookingType": "Buchungstyp",
  "productEditor.bookingTypeHint": "Privat = gesamte Unterkunft, Festpreis, Sperrung der Daten bei Genehmigung. Geteilt = mehrere Gruppen, Kosten anteilig pro Person und Tag. Sobald Reservierungen existieren, nicht mehr änderbar.",
  "productEditor.price": "Preis / Nacht ($)",
  "productEditor.bedrooms": "Schlafzimmer",
  "productEditor.bathrooms": "Badezimmer",
  "productEditor.maxGuests": "Max. Gäste",
  "productEditor.amenities": "Ausstattung (kommagetrennt)",
  "productEditor.amenitiesPlaceholder": "WiFi,Parking,Garten,Kamin",
  "productEditor.saveChanges": "Änderungen speichern",
  "productEditor.create": "Unterkunft erstellen",
  "productEditor.saving": "Speichern…",

  // ---- Admin console ----
  "admin.title": "Admin-Konsole",
  "admin.subtitle": "Website-Administrationswerkzeuge — Benutzer, Zugriffsstufen, Inhalte & Validierungsübersicht.",
  "admin.tabOverview": "Übersicht",
  "admin.tabUsers": "Benutzer",
  "admin.tabReservations": "Reservierungen",
  "admin.statUsers": "Benutzer gesamt",
  "admin.statProperties": "Unterkünfte",
  "admin.statReservations": "Reservierungen",
  "admin.statRevenue": "Umsatz (genehmigt)",
  "admin.resByStatus": "Reservierungen nach Status",
  "admin.usersByRole": "Benutzer nach Zugriffsstufe",
  "admin.addUser": "Benutzer hinzufügen",
  "admin.colName": "Name",
  "admin.colUsername": "Benutzername",
  "admin.colEmail": "E-Mail",
  "admin.colRole": "Rolle",
  "admin.colGroup": "Gruppe",
"admin.allRoles": "Alle Rollen",
"admin.allGroups": "Alle Gruppen",
"admin.unassigned": "Ohne Gruppe",
  "admin.colProperties": "Unterkünfte",
  "admin.colReservations": "Reservierungen",
  "admin.colStatus": "Status",
  "admin.colActions": "Aktionen",
  "admin.active": "Aktiv",
  "admin.disabled": "Deaktiviert",
  "admin.deleteUserTitle": "Diesen Benutzer löschen?",
  "admin.deleteUserBody": "Dies entfernt das Konto dauerhaft. Seine Aufenthalte bleiben als Verlauf in den Kalendern, seine Rechnungen bleiben ohne Verantwortlichen.",
  "admin.validateAny": "Beliebige Reservierung auf der Plattform validieren.",
  "admin.noPending": "Keine ausstehenden Reservierungen zu validieren.",
  "admin.recentDecisions": "Letzte Entscheidungen",

  // ---- User editor ----
  "userEditor.newTitle": "Benutzer erstellen",
  "userEditor.editTitle": "Benutzer bearbeiten",
  "userEditor.description": "Verwalten Sie Profil, Rolle und Zugriffsstufe eines Benutzers.",
  "userEditor.username": "Benutzername",
  "userEditor.firstName": "Vorname",
  "userEditor.lastName": "Nachname",
  "userEditor.email": "E-Mail",
  "userEditor.phone": "Telefon (optional)",
  "userEditor.role": "Rolle / Zugriffsstufe",
  "userEditor.active": "Konto aktiv",
  "userEditor.password": "Passwort",
  "userEditor.passwordKeep": "(leer lassen, um aktuelles beizubehalten)",
  "userEditor.passwordSet": "Passwort (leer = automatisch generieren)",
  "userEditor.passwordPlaceholder": "••••••••",
  "userEditor.saveChanges": "Änderungen speichern",
  "userEditor.create": "Benutzer erstellen",
  "userEditor.saving": "Speichern…",

  // ---- Language switcher ----
  "lang.label": "Sprache",
  "lang.changed": "Sprache aktualisiert",

  // ---- Toasts ----
  "toast.welcomeBack": "Willkommen zurück, {name}!",
  "toast.accountCreated": "Konto erstellt. Willkommen, {name}!",
  "toast.signedOut": "Abgemeldet",
  "toast.reservationRequested": "Reservierung angefragt! Der Gastgeber wird sie in Kürze validieren.",
  "toast.reservationCancelled": "Reservierung storniert",
  "toast.propertyCreated": "Unterkunft erstellt. Fügen Sie als Nächstes Fotos & Verfügbarkeit hinzu.",
  "toast.propertyUpdated": "Unterkunft aktualisiert",
  "toast.propertyDeleted": "Unterkunft gelöscht",
  "toast.propertyHidden": "Unterkunft verborgen",
  "toast.propertyPublished": "Unterkunft veröffentlicht",
  "toast.availAdded": "Verfügbarkeitsfenster hinzugefügt",
  "toast.availRemoved": "Entfernt",
  "toast.photoAdded": "Foto hinzugefügt",
  "toast.photoRemoved": "Foto entfernt",
  "toast.uploadFailed": "Upload fehlgeschlagen",
  "toast.userCreated": "Benutzer erstellt",
  "toast.userUpdated": "Benutzer aktualisiert",
  "toast.userDeleted": "Benutzer gelöscht",
  "toast.userEnabled": "Benutzer aktiviert",
  "toast.userDisabled": "Benutzer deaktiviert",
  "toast.reservationApproved": "Reservierung genehmigt",
  "toast.reservationRejected": "Reservierung abgelehnt",
  "toast.approved": "Genehmigt",
  "toast.rejected": "Abgelehnt",
  "toast.actionFailed": "Aktion fehlgeschlagen",
  "toast.couldNotCancel": "Konnte nicht storniert werden",
  "toast.couldNotUpdate": "Konnte nicht aktualisiert werden",
  "toast.couldNotDelete": "Konnte nicht gelöscht werden",
  "toast.couldNotSave": "Konnte nicht gespeichert werden",
  "toast.couldNotLoad": "Konnte nicht geladen werden",
  "toast.couldNotLoadUser": "Benutzer konnte nicht geladen werden",
  "toast.couldNotRemove": "Konnte nicht entfernt werden",
  "toast.couldNotCreate": "Reservierung konnte nicht erstellt werden",
  "toast.pleaseChooseDates": "Bitte wählen Sie Anreise- und Abreisedaten",

  // ---- Footer ----
  "footer.tagline": "Eine Buchungsplattform für Familienhäuser, Chalets und Wohnungen. Eigentümer stellen ein, Gäste reservieren, Administratoren validieren jeden Aufenthalt.",
  "footer.explore": "Entdecken",
  "footer.allProperties": "Alle Unterkünfte",
  "footer.publicPages": "Öffentliche Hausseiten",
  "footer.myTrips": "Meine Reisen",
  "footer.forHosts": "Für Gastgeber",
  "footer.host1": "Unterkunft mit Fotos & Daten einstellen",
  "footer.host2": "Verfügbarkeitsfenster verwalten",
  "footer.host3": "Reservierungsanfragen genehmigen oder ablehnen",
  "footer.roadmap": "Roadmap",
  "footer.roadmap1": "iOS- & Android-Apps",
  "footer.roadmap2": "WhatsApp-Gruppen-Push-Benachrichtigungen",
  "footer.roadmap3": "Kalendersynchronisierung & Zahlungen",
  "footer.rights": "Alle Rechte vorbehalten.",
  "footer.builtWith": "Erstellt mit Next.js, Prisma & gesalzenen gehashten Passwörtern.",

  // ---- Misc ----
  "night": "Nacht",
  "nights": "Nächte",
  "guests": "Gäste",
  "properties": "Unterkünfte",

  // ---- Auth (account pending) ----
  "auth.accountPending": "Ihr Konto wartet auf Freigabe. Ein Moderator wird es in Kürze freigeben — Sie können nach Genehmigung reservieren.",

  // ---- Roles (incl. moderator) ----
  "role.MODERATOR": "Moderator",

  // ---- User groups ----
  "group.FAMILY": "Familie",
  "group.GUEST": "Gast",
  "group.label": "Gruppe",
  "group.none": "Nicht zugewiesen",
  "group.familyDesc": "Angehörige, die das Familienhaus teilen — profitieren vom Familienrabatt und können als Ausgaben-Zahler erfasst werden.",
  "group.guestDesc": "Externe Urlaubsmitte — zahlen den Standard-Saisonpreis.",

  // ---- Validation ----
  "nav.moderator": "Moderation",
  "nav.expenses": "Ausgaben",
  "nav.dashboard": "Dashboard",
  "nav.notifications": "Benachrichtigungen",
  "mod.title": "Moderationsbereich",
  "mod.subtitle": "Alles, was auf eine Entscheidung wartet: neue Konten freischalten, Buchungsanfragen bestätigen und das Mitgliederverzeichnis pflegen.",
  "mod.pending": "Wartet auf Freigabe",
  "mod.pendingCount": "{count} ausstehend",
  "mod.noPending": "Keine Konten warten auf Validierung.",
  "mod.validateAs": "Freigeben als",
  "mod.validateFamily": "Als Familie freigeben",
  "mod.validateGuest": "Als Gast freigeben",
  "mod.validated": "Validiert",
  "mod.notValidated": "Nicht validiert",
  "mod.signedUp": "Registriert am",
  "mod.validatedBy": "Validiert von {name}",
  "toast.userValidated": "Konto validiert",
  "toast.ownershipTransferred": "Eigentum übertragen",
  "toast.roleUpdated": "Rolle aktualisiert",

  // ---- Notifications ----
  "notif.title": "Benachrichtigungskanäle",
  "notif.subtitle": "Wohin Registrierungs-/Reservierungs-/Ausgabenbenachrichtigungen gesendet werden. E-Mail- und WhatsApp-Anbieter sind austauschbar (standardmäßig Logs; in Produktion durch echtes SMTP / WhatsApp Business API ersetzen).",
  "notif.addEmail": "E-Mail hinzufügen",
  "notif.addWhatsapp": "WhatsApp hinzufügen",
  "notif.target": "Ziel (E-Mail oder Telefon)",
  "notif.label": "Bezeichnung (optional)",
  "notif.kind": "Art",
  "notif.EMAIL": "E-Mail",
  "notif.WHATSAPP": "WhatsApp",
  "notif.active": "Aktiv",
  "notif.inactive": "Inaktiv",
  "notif.history": "Versandverlauf",
  "notif.noChannels": "Keine Kanäle konfiguriert. Fügen Sie oben ein E-Mail- oder WhatsApp-Ziel hinzu.",
  "notif.noHistory": "Noch keine Benachrichtigungen versendet.",
  "toast.channelAdded": "Kanal hinzugefügt",
  "toast.channelRemoved": "Kanal entfernt",
  "toast.channelUpdated": "Kanal aktualisiert",

  // ---- Season pricing ----
  "season.highSeasonRate": "Hauptsaison-Tarif ($/Nacht)",
  "season.highSeasonRateHint": "Angewendet auf Tage innerhalb eines Hauptsaison-Zeitraums. Auf 0 lassen, um überall den Basistarif zu verwenden.",
  "season.familyDiscount": "Familienrabatt (%)",
  "season.familyDiscountHint": "Prozentualer Rabatt auf den Nachtarif für Benutzer der Gruppe FAMILY. 0 = kein Rabatt.",
  "season.ranges": "Hauptsaison-Zeiträume",
  "season.addRange": "Hauptsaison-Zeitraum hinzufügen",
  "season.rangeName": "Name des Zeitraums",
  "season.from": "Von",
  "season.to": "Bis",
  "season.current": "Aktuelle Hauptsaison-Zeiträume ({count})",
  "season.empty": "Keine Hauptsaison-Zeiträume — jeder Tag verwendet den Basistarif.",
  "season.highSeason": "Hauptsaison",
  "season.lowSeason": "Nebensaison",
  "season.highSeasonBadge": "Hauptsaison {rate}",
  "toast.seasonAdded": "Hauptsaison-Zeitraum hinzugefügt",
  "toast.seasonRemoved": "Hauptsaison-Zeitraum entfernt",
  "detail.seasonNote": "Saisontarife gelten pro Tag; das Familienrabatt wird für Benutzer der Gruppe FAMILY angewendet.",

  // ---- Expenses ----
  "expenses.title": "Ausgaben & Kosten",
  "expenses.subtitle": "Verfolgen Sie feste Ausgaben (Steuern, Strom, Gas, Internet, Abonnements) und einmalige Ausgaben (Wartung). Erfassen Sie, wer jede Rechnung bezahlt hat.",
  "expenses.tabPending": "Ausstehend ({count})",
  "expenses.tabPaid": "Bezahlt ({count})",
  "expenses.tabDueSoon": "Bald fällig ({count})",
  "expenses.badgeWrittenOff": "Abgeschrieben",
  "expenses.add": "Ausgabe hinzufügen",
  "expenses.product": "Unterkunft",
  "expenses.type": "Typ",
  "expenses.typeFixed": "Fest (wiederkehrend)",
  "expenses.typePunctual": "Einmalig",
  "expenses.category": "Kategorie",
  "expenses.cat.TAXES": "Steuern (Haus & Grund)",
  "expenses.cat.ELECTRICITY": "Strom",
  "expenses.cat.GAS": "Gas",
  "expenses.cat.INTERNET": "Internet-Abo",
  "expenses.cat.SUBSCRIPTION": "Service-Abo",
  "expenses.cat.MAINTENANCE": "Wartung / Reparaturen",
  "expenses.cat.CUSTOM": "Sonstiges",
  "expenses.customLabel": "Eigene Bezeichnung",
  "expenses.description": "Beschreibung",
  "expenses.amount": "Betrag ($)",
  "expenses.dueDate": "Fälligkeitsdatum",
  "expenses.periodStart": "Periodenbeginn",
  "expenses.periodEnd": "Periodenende",
  "expenses.status": "Status",
  "expenses.PENDING": "Ausstehend",
  "expenses.PAID": "Bezahlt",
  "expenses.markPaid": "Zahlung erfassen",
  "expenses.paidBy": "Bezahlt von",
  "expenses.paidDate": "Bezahlt am",
  "expenses.paymentMethod": "Zahlungsmethode",
  "expenses.method.CASH": "Bar",
  "expenses.method.BANK_TRANSFER": "Banküberweisung",
  "expenses.method.CARD": "Karte",
  "expenses.method.OTHER": "Andere",
  "expenses.selectPayer": "Familien-Zahler auswählen",
  "expenses.noFamilyPayers": "Noch keine validierten FAMILY-Benutzer. Validieren Sie zuerst einen Benutzer als Familie.",
  "expenses.noExpenses": "Noch keine Ausgaben.",
  "expenses.noPaid": "Noch keine Zahlungen erfasst.",
  "expenses.totalPending": "Gesamt ausstehend",
  "expenses.totalPaid": "Gesamt bezahlt",
  "toast.expenseAdded": "Ausgabe hinzugefügt",
  "toast.expenseUpdated": "Ausgabe aktualisiert",
  "toast.expenseDeleted": "Ausgabe gelöscht",
  "toast.expensePaid": "Zahlung erfasst",
  "toast.onlyFamilyCanPay": "Nur Benutzer der Gruppe FAMILY können als Zahler erfasst werden",

  // ---- Dashboard ----
  "dash.title": "Dashboard",
  "dash.subtitle": "Wer wann was bezahlt hat. Familienbeiträge, Ausgabenaufschlüsselungen und ausstehende Posten auf einen Blick.",
  "dash.summaryTotalPaid": "Gesamt bezahlt",
  "dash.summaryTotalPending": "Gesamt ausstehend",
  "dash.summaryPayments": "Erfasste Zahlungen",
  "dash.summaryPendingUsers": "Benutzer warten auf Validierung",
  "dash.byPayer": "Bezahlt nach Familienmitglied",
  "dash.byCategory": "Bezahlt nach Kategorie",
  "dash.recentPayments": "Letzte Zahlungen (wer wann was bezahlt hat)",
  "dash.pendingExpenses": "Ausstehende Ausgaben",
  "dash.noPayments": "Noch keine Zahlungen erfasst.",
  "dash.noPendingExpenses": "Keine ausstehenden Ausgaben. Alles ist bezahlt!",
  "dash.paidOn": "Bezahlt am",
  "dash.by": "von",
  "dash.noContributions": "Noch keine Beiträge erfasst.",

  // ---- Admin role-preview (impersonation) ----
  "preview.as": "Als Benutzer vorschauen",
  "preview.title": "Als Benutzer vorschauen",
  "preview.hint": "Nutzen Sie die App mit Rolle und Berechtigungen eines anderen Benutzers. Ihre Admin-Sitzung bleibt im Hintergrund aktiv.",
  "preview.preview": "Vorschau",
  "preview.exit": "Vorschau beenden",
  "preview.exited": "Vorschau beendet — Sie sind wieder Admin.",
  "preview.started": "Vorschau als {name}",
  "preview.banner": "Vorschau als {name} ({role})",

  // ---- User editor: group + validated ----
  "userEditor.group": "Benutzergruppe",
  "userEditor.validated": "Konto validiert",
  "userEditor.validatedHint": "Validierte Benutzer können Reservierungen vornehmen.",
  "userEditor.generate": "Generieren",
"userEditor.createdTitle": "Benutzer erstellt",
"userEditor.createdHint": "Kopieren Sie Benutzername und Passwort jetzt — das Passwort wird nicht erneut angezeigt.",
"userEditor.copy": "Kopieren",
"userEditor.copied": "Kopiert",
"userEditor.done": "Fertig",
"userEditor.optional": "(optional)",
"userEditor.maskedNote":
  "Personenbezogene Daten werden aus Datenschutzgründen maskiert angezeigt. Zum Ändern neu eingeben.",

  // ---- Ausgaben: Übersicht, Schulden & Rechnungen ----
  "expenses.cardToPay": "Zu zahlen",
  "expenses.cardOverdue": "Überfällig",
  "expenses.cardDueSoon": "Fällig bald (30 Tage)",
  "expenses.tabAll": "Alle",
  "expenses.tabToPay": "Zu zahlen ({count})",
  "expenses.tabDebts": "Schulden ({count})",
  "expenses.tabWrittenOff": "Abgeschrieben ({count})",
  "expenses.badgePending": "Zu zahlen",
  "expenses.badgePaid": "Bezahlt",
  "expenses.paidLateBadge": "Verspätet bezahlt",
  "expenses.dayLate": "1 Tag überfällig",
  "expenses.daysLate": "{count} Tage überfällig",
  "expenses.assignedTo": "Zugewiesen an",
  "expenses.assignToHint": "Das Familienmitglied, das für diese Rechnung verantwortlich ist",
  "expenses.unassigned": "Nicht zugewiesen",
  "expenses.writeOff": "Abschreiben",
  "expenses.writeOffTitle": "Diese Schuld abschreiben",
  "expenses.writeOffDesc": "Die Rechnung bleibt im Verlauf, gilt aber nicht mehr als geschuldet.",
  "expenses.writeOffNote": "Grund (optional)",
  "expenses.writeOffConfirm": "Abschreiben",
  "expenses.debtsSummary": "Offene Schulden pro Mitglied",
  "expenses.bills": "{count} Rechnung(en)",
  "expenses.noWrittenOff": "Nichts wurde abgeschrieben.",
  "expenses.noDebts": "Keine überfälligen Rechnungen — alles im Lot.",
  "expenses.invoice": "Rechnung",
  "toast.writeOffDone": "Schuld abgeschrieben",
  "invoice.title": "Rechnung",
  "invoice.number": "Rechnungsnr.",
  "invoice.issueDate": "Ausstellungsdatum",
  "invoice.dueDate": "Fälligkeitsdatum",
  "invoice.from": "Von",
  "invoice.billTo": "An",
  "invoice.description": "Beschreibung",
  "invoice.category": "Kategorie",
  "invoice.period": "Zeitraum",
  "invoice.total": "Gesamt",
  "invoice.status": "Status",
  "invoice.status.PENDING": "Fällig",
  "invoice.status.OVERDUE": "Überfällig",
  "invoice.status.PAID": "Bezahlt",
  "invoice.status.WRITTEN_OFF": "Abgeschrieben",
  "invoice.paidOn": "Bezahlt am",
  "invoice.notes": "Notizen",
  "invoice.print": "Drucken",
  "invoice.back": "Zurück zu den Ausgaben",
  "invoice.notFound": "Rechnung nicht gefunden oder Zugriff verweigert.",

  // ---- Familien & Gruppen ----
  "mod.tabValidation": "Validierung",
  "mod.tabGroups": "Familien & Gruppen",
  "mod.tabOwnership": "Eigentum",
  "mod.tabUsers": "Benutzer",
  "mod.tabReview": "Zu prüfen",
"mod.reviewEmpty": "Nichts offen — alles erledigt.",
"mod.queueBookings": "Buchungsanfragen",
"mod.queueAccounts": "Konten",
  "mod.tabBookings": "Buchungsanfragen",
"mod.noPendingBookings": "Keine Buchungsanfrage offen.",
"mod.bookingsHint": "Bestätigen oder lehnen Sie die Anfragen unten ab. Bestätigen belegt die Tage, Ablehnen gibt sie frei.",
"permissions.feature.bookings.validate": "Buchungsanfragen bestätigen / ablehnen",
"admin.noUsers": "Keine Benutzer entsprechen den Filtern.",
"admin.disable": "Konto deaktivieren",
"admin.enable": "Konto aktivieren",
"invitations.family": "Familie (optional)",
"invitations.noFamily": "Keine Familie",
"invitations.inviteAnother": "Weiteren Gast einladen",
"mod.ownershipHint": "Geben Sie einem Mitglied ein Haus. Der neue Eigentümer erhält die volle Kontrolle und kann dort Personal zuweisen.",
"mod.currentOwner": "Eigentümer",
"mod.transferSelect": "Neuen Eigentümer wählen…",
"mod.transferApply": "Übertragen",
"mod.transferConfirm": "'{house}' an {name} übertragen?",
"mod.noHouses": "Noch keine Häuser.",
  "mod.chooseFamily": "Welche Familie?",
  "mod.validateInto": "Als {group} validieren",
  "groups.subtitle": "Häuser gehören zu Familien. Mitglieder einer Familie erhalten den Familienrabatt auf die Objekte ihrer Familie.",
  "groups.create": "Familie erstellen",
  "groups.name": "Familienname",
  "groups.namePlaceholder": "z. B. Familie Martin",
  "groups.members": "{count} Mitglied(er)",
  "groups.properties": "{count} Objekt(e)",
  "groups.addMember": "Mitglied hinzufügen",
  "groups.removeMember": "Aus Familie entfernen",
  "groups.removeConfirm": "{name} aus der Familie entfernen? Er/Sie wird Gast.",
  "groups.rename": "Umbenennen",
  "groups.delete": "Familie löschen",
  "groups.deleteConfirm": "Die leere Familie \"{name}\" löschen?",
  "groups.guests": "Gäste",
  "groups.guestsHint": "Validierte Benutzer außerhalb jeder Familie.",
  "groups.noFamilies": "Noch keine Familien — erstellen Sie eine, um Mitglieder zu gruppieren.",
  "groups.candidatesEmpty": "Keine validierten Benutzer verfügbar.",
  "groups.unassignedFam": "Familienmitglieder ohne Familie",
  "groups.memberAdded": "{name} ist {family} beigetreten",
  "groups.memberRemoved": "{name} hat die Familie verlassen",
  "groups.familyCreated": "Familie erstellt",
  "groups.familyRenamed": "Familie umbenannt",
  "groups.familyDeleted": "Familie gelöscht",

  // ---- Kalender im Dashboard ----
  "calendar.title": "Verfügbarkeit & Buchungen",
  "calendar.chooseProperty": "Objekt wählen",
  "calendar.legendAvailable": "Verfügbar",
  "calendar.legendBookedPrivate": "Gebucht — privat",
  "calendar.legendBookedShared": "Gebucht — geteilt",
  "calendar.legendPending": "Anfrage offen",
  "calendar.legendMixed": "Gebucht + offen",
  "calendar.legendOff": "Nicht verfügbar / vergangen",
  "calendar.pendingShort": "Offen",
  "calendar.mixedShort": "Gebucht + offen",
  "calendar.availableShort": "Verfügbar",
  "calendar.passedShort": "Vergangen",
  "calendar.noProperties": "Keine aktiven Objekte.",
  "calendar.viewMonth": "Monat",
  "calendar.viewYear": "Jahr",
  "calendar.typeHint": "Buchungstyp dieses Objekts: privat = exklusiv, geteilt = Umlage.",
  "calendar.bookMonth": "Buchung in diesem Monat hinzufügen",
  "calendar.daySuffix": "T",
  "calendar.daySuffixP": "a",
  "calendar.dayDetails": "Buchungen am {date}",
  "calendar.exclusiveNote": "Dieser Tag ist exklusiv durch einen Privataufenthalt belegt.",
  "calendar.addHere": "Buchung an diesem Tag hinzufügen",
  "calendar.status.APPROVED": "Bestätigt",
  "calendar.status.PENDING": "Ausstehend",
  "calendar.typeBlockedHint": "Dieser Buchungstyp ist für die ausgewählten Termine nicht verfügbar — eine genehmigte Buchung belegt sie. Wählen Sie den anderen Typ oder andere Termine.",
  "calendar.cancelBooking": "Diese Buchung stornieren",
  "calendar.cancelTitle": "Diese Buchung stornieren?",
  "calendar.cancelAck": "Die Bestätigung storniert diese Buchung und gibt ihre Termine sofort für alle frei. Dies kann hier nicht rückgängig gemacht werden.",
  "calendar.cancelConfirm": "Ja, stornieren",
  "calendar.cancelled": "Buchung storniert",


  "calendar.clickHintYear": "Klicken Sie in einem beliebigen Monat auf einen Tag, um eine Buchung hinzuzufügen, oder auf den Monatsnamen.",
  "calendar.addBooking": "Buchung hinzufügen",
  "calendar.bookDay": "Neue Buchung am {date}",
  "calendar.bookFor": "Für",
  "calendar.myself": "Mich selbst",
  "calendar.approvedHint": "Die Buchung wird als BESTÄTIGT erstellt: Die Termine sind sofort für alle gesperrt.",
  "calendar.bookingAdded": "Buchung hinzugefügt",
  "calendar.clickHint": "Klicken Sie auf einen Tag, um eine Buchung hinzuzufügen — klicken Sie auf einen belegten Tag, um dessen Buchungen zu sehen.",
  "calendar.alreadyBooked": "hat diese Termine bereits gebucht",

  // ---- Gästeeinladungen ----
  "mod.tabInvitations": "Gästeeinladungen",
  "invitations.inviteGuest": "Gast einladen",
  "invitations.email": "Gast-E-Mail",
  "invitations.firstName": "Vorname",
  "invitations.lastName": "Nachname",
  "invitations.send": "Einladung erstellen & Link kopieren",
  "invitations.sent": "Einladung erstellt — Link in die Zwischenablage kopiert",
  "invitations.linkReady": "Teilen Sie diesen Link mit {email}:",
  "invitations.copy": "Link kopieren",
  "invitations.copied": "Link kopiert",
  "invitations.sendHint": "Der Gast öffnet den Link, wählt Benutzername und Passwort und das Konto wird sofort aktiviert.",
  "invitations.history": "Gesendete Einladungen",
  "invitations.none": "Noch keine Einladungen gesendet.",
  "invitations.accepted": "Angenommen",
  "invitations.pending": "Ausstehend",
  "invitations.expired": "Abgelaufen",
  "invitations.by": "von",
  "invitations.revoke": "Einladung widerrufen",
  "invitations.revoked": "Einladung widerrufen",
  "invite.title": "Sie wurden zu HomeBook eingeladen",
  "invite.hello": "Hallo {name}!",
  "invite.username": "Benutzername wählen",
  "invite.usernameRules": "3-30 Zeichen, beginnt mit einem Buchstaben (Buchstaben, Ziffern, _ oder -).",
  "invite.password": "Passwort wählen",
  "invite.confirmPassword": "Passwort bestätigen",
  "invite.createAccount": "Konto erstellen",
  "invite.welcome": "Willkommen an Bord! Sie sind angemeldet.",
  "invite.invalid": "Diese Einladung ist ungültig, bereits verwendet oder abgelaufen.",
  "invite.passwordMismatch": "Passwörter stimmen nicht überein",

  // ---- Berechtigungsmatrix ----
  "admin.tabPermissions": "Berechtigungen",
  "permissions.title": "Rollenberechtigungen",
  "permissions.hint": "Legen Sie fest, welche Funktionen jede Rolle nutzen darf. Änderungen gelten sofort für alle Konten dieser Rolle.",
  "permissions.feature": "Funktion",
  "permissions.updated": "Berechtigung aktualisiert",
  "permissions.feature.expenses.view": "Ausgabenübersicht anzeigen",
  "permissions.feature.expenses.manage": "Ausgaben erstellen / bearbeiten / löschen",
  "permissions.feature.expenses.payAssigned": "Ihnen zugewiesene Ausgaben bezahlen oder abschreiben",
  "permissions.feature.guests.invite": "Gäste einladen (Konten erstellen, Links senden)",
  "permissions.feature.users.validate": "Ausstehende Konten validieren",
  "permissions.feature.families.manage": "Familien & Gruppen verwalten",
  "permissions.feature.products.manage": "Objekte erstellen / bearbeiten",
  "permissions.feature.calendar.view": "Buchungskalender anzeigen",
  "permissions.feature.bookings.manage": "Buchungen aus dem Kalender hinzufügen",
  "settings.currencyTitle": "Anzeigewährung",
  "settings.currencyHint": "Wird überall dort verwendet, wo Preise angezeigt werden. Änderungen aktualisieren alle Beträge (keine Umrechnung gespeicherter Werte).",
  "settings.currencySaved": "Währung geändert auf {currency}",
  "expenses.breakdownTitle": "Aufteilung Fixkosten / Einmalig",
  "expenses.scopeYear": "Jährlich",
  "expenses.scopeMonth": "Monatlich",
  "expenses.fixedTotal": "Fixkosten",
  "expenses.punctualTotal": "Einmalig",
  "expenses.periodTotal": "Gesamt",
  "expenses.matrixMonth": "Monat",
  "expenses.chartTitle": "Ausgaben pro Monat",
"expenses.topCategories": "Wohin das Geld fließt",
"expenses.dueInDays": "fällig in {count} Tagen",
"expenses.dueTomorrow": "morgen fällig",
"expenses.dueToday": "heute fällig",
  "expenses.allHouses": "Alle Häuser",
  "expenses.changeAssignment": "Zuweisung ändern",
"expenses.assignmentSaved": "Zuweisung gespeichert",
"expenses.unassign": "Kein Verantwortlicher (leeren)",

  // ---- Benachrichtigungen ----
  "notif.type.USER_SIGNUP": "Neue Anmeldung",
  "notif.type.USER_VALIDATED": "Konto validiert",
  "notif.type.GUEST_INVITED": "Gast eingeladen",
  "notif.type.RESERVATION_REQUEST": "Buchungsanfrage",
  "notif.type.RESERVATION_APPROVED": "Buchung bestätigt",
  "notif.type.RESERVATION_REJECTED": "Buchung abgelehnt",
  "notif.type.BOOKING_ADDED": "Buchung hinzugefügt",
  "notif.type.EXPENSE_ASSIGNED": "Ausgabe zugewiesen",
  "notif.type.EXPENSE_PAID": "Ausgabe bezahlt",
  "notif.type.TEST": "Test",
  "notif.channelsTitle": "Kanäle",
  "notif.editEvents": "Ereignisse bearbeiten",
  "notif.sendTest": "Test senden",
  "notif.testOk": "Test versendet — prüfen Sie den Verlauf und das Ziel-Postfach/Log",
  "notif.testNoProvider": "Test aufgezeichnet, aber kein aktiver Anbieter hat ihn zugestellt (Konsolen-Simulation)",
  "notif.typesTitle": "Abonnierte Ereignisse",
  "notif.typesHint": "Der Kanal erhält nur die ausgewählten Ereignistypen. Nichts auswählen = alle empfangen.",
  "notif.allTypes": "Alle Ereignisse",
  "notif.allTypesFallback": "Nichts ausgewählt — der Kanal erhält alle Ereignistypen.",
  "notif.filterType": "Ereignistyp",
  "notif.all": "Alle",

  // ---- Multi-level navigation + Home screen ----
  "nav.back": "Zurück",
  "nav.home": "Start",
  "nav.level.browse": "Häuser",
  "nav.level.product": "Haus",
  "nav.level.my-reservations": "Meine Reisen",
  "nav.level.host": "Vermieterpanel",
  "nav.level.moderator": "Moderation",
  "nav.level.expenses": "Rechnungen",
  "nav.level.dashboard": "Dashboard",
  "nav.level.notifications": "Benachrichtigungen",
  "nav.level.admin": "Admin-Konsole",
  "home.welcome": "Willkommen zurück, {name}",
  "home.welcomeGuest": "Willkommen bei HomeBook",
  "home.subtitle": "Alles mit einem Tipp — wähle, wohin du willst.",
  "home.browse": "Häuser ansehen",
  "home.browseHint": "Fotos, Preise und der gemeinsame Kalender",
  "home.myTrips": "Meine Reisen",
  "home.myTripsHint": "Deine Anfragen, vergangene und kommende Aufenthalte",
  "home.host": "Vermieterpanel",
  "home.hostHint": "Häuser, Fotos und Buchungen verwalten",
  "home.moderator": "Zu prüfen",
  "home.moderatorHint": "Zu genehmigende Buchungen und zu bestätigende Konten",
  "home.reviewBadge": "offen",
  "home.expenses": "Rechnungen",
  "home.expensesHint": "Wer was bezahlt hat, Schulden und Rechnungen",
  "home.dashboard": "Dashboard",
  "home.dashboardHint": "Summen nach Zahler, Monat und Kategorie",
  "home.notifications": "Benachrichtigungen",
  "home.notificationsHint": "Alarmkanäle und Versandverlauf",
  "home.admin": "Admin-Konsole",
  "home.adminHint": "Mitglieder, Häuser, Rollen und Einstellungen",
  "home.docsHint": "Wie alles funktioniert, in einfachen Worten",
  "preview.bannerNote": "Du siehst die App als anderes Mitglied.",

  // ---- Occupancy, stay members, dashboard rename ----
  "dash.occupancy": "Auslastung",
  "dash.occupancyHint": "Übernachtungen pro Haus und pro Mitglied",
  "dash.byHouse": "Übernachtungen pro Haus",
  "dash.byMember": "Übernachtungen pro Mitglied",
  "dash.totalNights": "Gebuchte Nächte",
  "dash.noOccupancy": "Noch kein bestätigter Aufenthalt.",
  "dash.memberSpend": "Anteil an den Aufenthaltskosten",
  "dash.spendHint": "Der Anteil jedes Mitglieds an den Aufenthalten, an denen es teilnimmt",
  "stay.members": "Mitglieder",
  "stay.membersHint": "Alle Mitglieder dieses privaten Aufenthalts teilen den Preis zu gleichen Teilen.",
  "stay.member": "Mitglied",
  "stay.addMember": "Mitglied hinzufügen",
  "stay.removeMember": "Entfernen",
  "stay.organiser": "Organisator",
  "stay.share": "Anteil",
  "stay.each": "{amount} pro Person",
  "stay.locked": "Bestätigter Aufenthalt — Mitglieder sind gesperrt.",
  "stay.lockedPerm": "Du kannst sie weiterhin ändern (Berechtigung: bestätigte Aufenthalte bearbeiten).",
  "stay.sharedSingle": "Ein geteilter Aufenthalt ist individuell: ein Mitglied pro Buchung.",
  "stay.memberAdded": "Mitglied zum Aufenthalt hinzugefügt",
  "stay.memberRemoved": "Mitglied aus dem Aufenthalt entfernt",
  "stay.count": "{count} Mitglieder",
  "stay.people": "{count} Personen",
  "permissions.feature.bookings.editAfterApproval": "Mitglieder eines bestätigten privaten Aufenthalts hinzufügen/entfernen",
  "booking.stayMembers": "Wer ist dabei? (privat)",
  "booking.stayMembersHint": "Wähle die Mitglieder, die das Haus teilen. Der Preis wird gleichmäßig geteilt.",
  "booking.you": "Du",

  // ---- Dashboard filter + money charts ----
  "dash.filterHouse": "Nach Haus filtern",
  "dash.allHouses": "Alle Häuser",
  "dash.expensesPerHouse": "Ausgaben pro Haus",
  "dash.expensesByCategory": "Ausgaben nach Kategorie",
  "dash.paidPerMember": "Bezahlt pro Mitglied",
  "dash.owedPerMember": "Ausgaben pro Mitglied (verantwortlich)",
  "dash.totalBilled": "abgerechnet",
  "dash.noExpenses": "Noch nichts abgerechnet.",
  "dash.chartsHint": "Jede Zahl und jedes Diagramm folgt dem Hausfilter oben.",
};
